import React from 'react';
import { Banner, StatusPill } from '@/ds';
import { ApiError } from '@/api';
import { useT } from '@/i18n';
import type { LabError, LabResponse, LabRole, RequestedConfiguration, Step } from '@/api/reqventos-agent-lab-types';
import styles from './workspace.module.css';
export const money = (value: number) => `USD ${(value / 1_000_000).toFixed(6).replace(/0{1,4}$/, '')}`;
export const active = (state?: string) => state === 'queued' || state === 'running';
export const uuid = () => crypto.randomUUID();
export const copy = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
export function errorValue(error: unknown): LabError | null {
  return error instanceof ApiError && error.data && typeof error.data === 'object' && 'code' in error.data ? error.data as LabError : null;
}
export function Failure({ error }: { error: unknown }) {
  const t = useT();
  if (!error) return null;
  const safe = errorValue(error);
  return <div role="alert"><Banner tone="danger" title={t(`agentLab.error.${safe?.code ?? 'REQUEST_FAILED'}`)}>
    {safe && <><p>{t(`agentLab.action.${safe.next_action}`)}</p><code>{safe.code} · {safe.stage}{safe.json_pointer ? ` · ${safe.json_pointer}` : ''}</code>
      {safe.trace_id && <p className={styles.mono}>{t('agentLab.traceId')}: {safe.trace_id}</p>}</>}
  </Banner></div>;
}
export function State({ value }: { value: string }) {
  const t = useT();
  const tone = ['failed', 'FAIL'].includes(value) ? 'danger' : ['uncertain', 'blocked', 'INCONCLUSIVE', 'incomplete'].includes(value) ? 'warning'
    : ['running', 'queued'].includes(value) ? 'progress' : ['completed', 'PASS'].includes(value) ? 'success' : 'neutral';
  return <StatusPill tone={tone}>{t(`agentLab.state.${value}`)}</StatusPill>;
}
export function Json({ value }: { value: unknown }) { return <pre className={styles.json}>{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</pre>; }
export function Disclosure({ title, children, open, onToggle }: { title: string; children: React.ReactNode; open?: boolean; onToggle?: (open: boolean) => void }) {
  return <details className={styles.disclosure} open={open} onToggle={onToggle ? e => onToggle(e.currentTarget.open) : undefined}>
    <summary>{title}</summary><div className={styles.disclosureBody}>{children}</div>
  </details>;
}
export function Field({ label, children, hint }: { label: string; children: React.ReactElement; hint?: string }) {
  const id = React.useId();
  return <div className={styles.field}><label htmlFor={id}>{label}</label>{React.cloneElement(children, { id, 'aria-describedby': hint ? `${id}-hint` : undefined })}
    {hint && <small id={`${id}-hint`}>{hint}</small>}</div>;
}
export function effectiveRole(role: LabRole, status: LabResponse<'status'>, requested: RequestedConfiguration) {
  const baseline = status.baseline_roles.find(item => item.role === role)!;
  const override = requested.role_overrides[role];
  return { model_id: override?.model_id ?? requested.global_model_id, effort: override?.effort ?? baseline.effort,
    max_output_tokens: override?.max_output_tokens ?? baseline.max_output_tokens };
}

export function Warnings({ warnings }: { warnings: Step['trace']['warnings'] }) {
  const t = useT();
  if (!warnings?.length) return null;
  return <Disclosure title={`${t('agentLab.warnings')} (${warnings.length})`}>
    <p>{t('agentLab.warningHint')}</p>
    {warnings.map((warning, i) => <div className={styles.finding} key={i}>
      <strong>{t(`agentLab.role.${warning.role}`)} · {t(`agentLab.warning.${warning.code}`)}</strong>
      <p className={styles.mono}>{warning.path}</p>
    </div>)}
  </Disclosure>;
}
